import React, { useState } from 'react'
import { Target, FileText, TrendingUp, Clock, User, Award, Calendar, ChevronRight, Zap, BarChart3, ChevronDown, ChevronUp, BookOpen } from 'lucide-react'
import { Goal } from '../types'

interface GoalRendererProps {
  goal: Goal
  index: number
  onSourceClick?: (pageNumber: number) => void
}

export const GoalRenderer: React.FC<GoalRendererProps> = ({ goal, index, onSourceClick }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isPresentLevelExpanded, setIsPresentLevelExpanded] = useState(false)
  const [isMainContentExpanded, setIsMainContentExpanded] = useState(false)
  
  const renderSourceReference = (pageNumber?: number) => {
    if (!pageNumber || !onSourceClick) return null
    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSourceClick(pageNumber)
        }}
        className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-blue-600"
        title={`Go to page ${pageNumber} in PDF`}
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      </button>
    )
  }

  const renderDetailSection = (title: string, content?: string | string[], icon?: React.ComponentType<any>) => {
    if (!content) return null
    
    const IconComponent = icon
    const isArray = Array.isArray(content)
    const isEmpty = isArray ? content.length === 0 : !content.trim()
    
    if (isEmpty) return null

    return (
      <div className="mb-3">
        <div className="flex items-center mb-2">
          {IconComponent && <IconComponent className="h-4 w-4 text-gray-500 mr-2" />}
          <h6 className="text-sm font-medium text-gray-700">{title}</h6>
        </div>
        <div className="ml-6">
          {isArray ? (
            <ul className="list-disc list-inside space-y-1">
              {content.map((item, idx) => (
                <li key={idx} className="text-gray-600 text-sm leading-relaxed">{item}</li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 text-sm leading-relaxed">{content}</p>
          )}
        </div>
      </div>
    )
  }

  // Generate a title for the goal
  const getGoalTitle = () => {
    if (goal.short_description) {
      return goal.short_description
    }
    if (goal.area) {
      return `${goal.area} Goal`
    }
    return `Goal ${index + 1}`
  }


  // Helper function to determine if text should be expandable (longer than ~2 sentences)
  const isTextLong = (text: string) => {
    if (!text) return false
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0)
    return sentences.length > 2 || text.length > 200
  }

  // Helper function to get short version of standard for header display
  const getShortStandard = (standard?: string) => {
    if (!standard || standard.trim() === '' || standard.toLowerCase().includes('not assigned')) {
      return null
    }
    // If standard is longer than 30 characters, truncate it
    if (standard.length > 30) {
      return standard.substring(0, 27) + '...'
    }
    return standard
  }

  // Check if we have additional details to show (now includes Success Criteria and full standard)
  const hasAdditionalDetails = goal.target || goal.measurement || goal.timeline || goal.teacher || 
                              goal.objectives || goal.service_connection || goal.progress_reporting || 
                              (goal.standard && goal.standard.length > 30)

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      {/* Goal Header - Always Clickable */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsMainContentExpanded(!isMainContentExpanded)}
      >
        <div className="flex-1 min-w-0">
          {/* Goal Type and Area */}
          <div className="flex items-center mb-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <Target className="h-4 w-4 mr-1.5" />
              {goal.area || 'Academic Goal'}
            </span>
            {getShortStandard(goal.standard) && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                {getShortStandard(goal.standard)}
              </span>
            )}
            {goal.source_page && renderSourceReference(goal.source_page)}
          </div>
          
          {/* Goal Title */}
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {getGoalTitle()}
            </h3>
          </div>
          
          {/* Key Details Preview */}
          <div className="flex items-center text-sm text-gray-600 space-x-6">
            {goal.milestones && goal.milestones.length > 0 && (
              <div className="flex items-center">
                <span className="text-xs font-medium text-amber-600">
                  {goal.milestones.length} milestone{goal.milestones.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            
            {goal.teacher && (
              <div className="flex items-center">
                <User className="h-4 w-4 mr-1" />
                <span>{goal.teacher}</span>
              </div>
            )}
            
            {goal.timeline && (
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                <span>{goal.timeline}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex-shrink-0 ml-4">
          {isMainContentExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isMainContentExpanded && (
        <div className="border-t border-gray-100 px-4 pb-4">
          <div className="pt-4 space-y-6">
          {/* 1. Annual Goal (Top Priority) */}
          {goal.description && (
            <div>
              <div className="flex items-center mb-3">
                <Award className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="text-base font-medium text-gray-800">Annual Goal</h4>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                <p className="text-green-900 text-base font-medium leading-relaxed">{goal.description}</p>
              </div>
            </div>
          )}

          {/* 2. Benchmarks/Milestones */}
          {goal.milestones && goal.milestones.length > 0 && (
            <div>
              <div className="flex items-center mb-3">
                <ChevronRight className="h-5 w-5 text-amber-500 mr-2" />
                <h4 className="text-base font-medium text-gray-800">Benchmarks & Milestones</h4>
              </div>
              <div className="space-y-2">
                {goal.milestones.map((milestone, idx) => (
                  <div key={idx} className="flex items-start bg-amber-50 p-3 rounded-lg">
                    <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-amber-100 text-amber-700 mr-3 mt-0.5">
                      <span className="text-xs font-medium">{idx + 1}</span>
                    </div>
                    <p className="text-amber-800 text-sm leading-relaxed flex-1">{milestone}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3. Present Level (Expandable if long) */}
          {goal.baseline && (
            <div>
              <div className="flex items-center mb-3">
                <TrendingUp className="h-5 w-5 text-gray-500 mr-2" />
                <h4 className="text-base font-medium text-gray-800">Present Level</h4>
              </div>
              {isTextLong(goal.baseline) ? (
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <div className="p-4">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {isPresentLevelExpanded 
                        ? goal.baseline 
                        : goal.baseline.split(/[.!?]/)[0] + (goal.baseline.includes('.') || goal.baseline.includes('!') || goal.baseline.includes('?') ? '...' : '')}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsPresentLevelExpanded(!isPresentLevelExpanded)}
                    className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors text-gray-600 text-xs font-medium flex items-center justify-center"
                  >
                    {isPresentLevelExpanded ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        Show More
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 text-sm leading-relaxed">{goal.baseline}</p>
                </div>
              )}
            </div>
          )}

          {/* Collapsible Details Section (includes Success Criteria) */}
          {hasAdditionalDetails && (
            <div className="border-t border-gray-200 pt-4">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="flex items-center">
                  <FileText className="h-4 w-4 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">
                    Details
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>
              
              {isExpanded && (
                <div className="mt-4 space-y-4 bg-gray-50 p-4 rounded-lg">
                  {/* Success Criteria (moved here) */}
                  {renderDetailSection("Success Criteria", goal.target, Target)}
                  
                  {/* Full Academic Standard (if truncated in header) */}
                  {goal.standard && goal.standard.length > 30 && renderDetailSection("Academic Standard", goal.standard, BookOpen)}
                  
                  {/* Measurement */}
                  {renderDetailSection("Progress Measurement", goal.measurement, BarChart3)}
                  
                  {/* Timeline */}
                  {renderDetailSection("Timeline", goal.timeline, Calendar)}
                  
                  {/* Teacher/Provider */}
                  {renderDetailSection("Responsible Staff", goal.teacher, User)}
                  
                  {/* Short-term Objectives */}
                  {renderDetailSection("Short-term Objectives", goal.objectives, Award)}
                  
                  {/* Service Connection */}
                  {renderDetailSection("Supporting Services", goal.service_connection, Zap)}
                  
                  {/* Progress Reporting */}
                  {renderDetailSection("Progress Reporting", goal.progress_reporting, BarChart3)}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  )
}

export default GoalRenderer
